import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';
import type { YearBook } from './yearbook';

/**
 * «Карта інтересів»: where things go well (grades), what the student likes (their own marks, clubs,
 * achievements, the role in the class's season) — by area, with a hint what to try. It decides
 * nothing: the student chooses. «Випускники»: former students tell about professions.
 */
export type InterestArea =
  'tech' | 'it' | 'math' | 'nature' | 'words' | 'society' | 'art' | 'sport';

export interface AreaInfo {
  key: InterestArea;
  name: string;
  emoji: string;
  subjects: readonly string[];
  /** stems of club titles of the area */
  clubs: readonly string[];
  achievements: readonly string[];
  role?: string;
  professions: readonly string[];
}

export const INTEREST_AREAS: readonly AreaInfo[] = [
  {
    key: 'tech',
    name: 'Техніка й інженерія',
    emoji: '⚙️',
    subjects: ['Фізика', 'Технології', 'Дизайн і технології'],
    clubs: ['робот', 'конструю'],
    achievements: ['science'],
    role: 'engineer',
    professions: ['інженер-енергетик', 'конструктор дронів', 'архітектор', 'інженер-механік'],
  },
  {
    key: 'it',
    name: 'Цифрові технології',
    emoji: '💻',
    subjects: ['Інформатика'],
    clubs: ['python', 'програм', 'робот', 'медіа'],
    achievements: [],
    professions: [
      'розробник програм',
      'аналітик даних',
      'фахівець з кібербезпеки',
      'дизайнер інтерфейсів',
    ],
  },
  {
    key: 'math',
    name: 'Математика й логіка',
    emoji: '🧮',
    subjects: ['Математика', 'Алгебра', 'Геометрія', 'Алгебра і початки аналізу'],
    clubs: ['шах'],
    achievements: ['olympiad'],
    professions: ['економіст', 'фінансовий аналітик', 'актуарій', 'науковець-математик'],
  },
  {
    key: 'nature',
    name: 'Природа й здоров’я',
    emoji: '🌱',
    subjects: [
      'Біологія',
      'Біологія і екологія',
      'Хімія',
      'Пізнаємо природу',
      'Я досліджую світ',
      'Здоров’я, безпека та добробут',
    ],
    clubs: ['еколог', 'біолог'],
    achievements: ['science'],
    role: 'scientist',
    professions: ['лікар', 'біотехнолог', 'еколог', 'фармацевт'],
  },
  {
    key: 'words',
    name: 'Мови й слово',
    emoji: '✍️',
    subjects: [
      'Українська мова',
      'Українська література',
      'Зарубіжна література',
      'Англійська мова',
    ],
    clubs: ['english', 'медіа', 'дебат', 'театр'],
    achievements: [],
    role: 'chronicler',
    professions: ['журналіст', 'перекладач', 'редактор', 'копірайтер'],
  },
  {
    key: 'society',
    name: 'Суспільство й історія',
    emoji: '🏛️',
    subjects: ['Історія', 'Історія України', 'Всесвітня історія', 'Географія', 'Захист України'],
    clubs: ['дебат'],
    achievements: ['volunteering'],
    role: 'navigator',
    professions: ['юрист', 'дипломат', 'історик', 'урбаніст'],
  },
  {
    key: 'art',
    name: 'Мистецтво й творчість',
    emoji: '🎨',
    subjects: ['Мистецтво'],
    clubs: ['малюван', 'хор', 'театр'],
    achievements: ['art'],
    professions: ['дизайнер', 'ілюстратор', 'актор', 'музикант'],
  },
  {
    key: 'sport',
    name: 'Спорт і рух',
    emoji: '⚽',
    subjects: ['Фізична культура'],
    clubs: ['футбол', 'спорт'],
    achievements: ['sport'],
    professions: ['тренер', 'фізичний терапевт', 'спортивний лікар', 'інструктор'],
  },
];

export interface AreaScore {
  area: AreaInfo;
  /** how it goes, 0..1, from grades; null without grades */
  strength: number | null;
  /** what speaks for liking it, 0..1 */
  interest: number;
  liked: boolean;
  /** «Алгебра 10,5», «Шахи», «олімпіада» */
  evidence: string[];
  score: number;
}

const has = (title: string, stems: readonly string[]) =>
  stems.some((s) => title.toLowerCase().includes(s));

/** The map: every area with how it goes and how much it is liked, the strongest first. */
export function interestMap(book: YearBook, likes: readonly string[]): AreaScore[] {
  return INTEREST_AREAS.map((area) => {
    const subjects = book.subjects.filter((s) => area.subjects.includes(s.subject));
    const avg = subjects.length
      ? subjects.reduce((n, s) => n + s.average, 0) / subjects.length
      : null;
    const strength = avg === null ? null : Math.max(0, Math.min(1, (avg - 4) / 8));
    const clubs = book.clubs.filter((c) => has(c.title, area.clubs));
    const achievements = book.achievements.filter((a) => area.achievements.includes(a.category));
    const role = area.role !== undefined && book.season?.role === area.role;
    const liked = likes.includes(area.key);
    const interest = Math.min(
      1,
      (liked ? 0.5 : 0) + 0.25 * clubs.length + 0.2 * achievements.length + (role ? 0.15 : 0),
    );
    const evidence = [
      ...subjects.map((s) => `${s.subject} ${String(s.average).replace('.', ',')}`),
      ...clubs.map((c) => `${c.emoji ?? ''} ${c.title}`.trim()),
      ...achievements.map((a) => a.title),
    ];
    return {
      area,
      strength,
      interest,
      liked,
      evidence,
      score: (strength ?? 0.3) * 0.5 + interest * 0.5,
    };
  }).sort((a, b) => b.score - a.score);
}

export function useInterestLikes(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['interest-likes', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<string[]> =>
      must(await db.from('interest_likes').select('areas').eq('student_member_id', studentId))[0]
        ?.areas ?? [],
  });
}

export function useSetInterestLikes(schoolId: string, studentId: string) {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (areas: InterestArea[]) =>
      must(
        await db.from('interest_likes').upsert({
          school_id: schoolId,
          student_member_id: studentId,
          areas,
          updated_at: new Date().toISOString(),
        }),
      ),
    onSettled: () => void client.invalidateQueries({ queryKey: ['interest-likes', studentId] }),
  });
}

// ---------------------------------------------------------------------------------------------
// Alumni
// ---------------------------------------------------------------------------------------------
export interface Alumnus {
  id: string;
  fullName: string;
  graduatedYear: number;
  profession: string;
  area: InterestArea;
  university: string | null;
  story: string;
  emoji: string | null;
}

export interface AlumniAnswer {
  id: string;
  alumnusId: string;
  question: string;
  answer: string;
  askedBy: string | null;
}

export interface AlumniQuestion {
  id: string;
  alumnusId: string;
  question: string;
  answer: string | null;
  askedAt: string;
}

export function useAlumni(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['alumni', schoolId],
    enabled: schoolId !== '',
    queryFn: async () => {
      const [people, answers] = await Promise.all([
        db
          .from('alumni')
          .select('id, full_name, graduated_year, profession, area, university, story, emoji')
          .eq('school_id', schoolId)
          .order('graduated_year', { ascending: false }),
        db.rpc('alumni_answers', { p_school: schoolId }),
      ]);
      return {
        alumni: must(people).map((a): Alumnus => ({
          id: a.id,
          fullName: a.full_name,
          graduatedYear: a.graduated_year,
          profession: a.profession,
          area: a.area as InterestArea,
          university: a.university,
          story: a.story,
          emoji: a.emoji,
        })),
        answers: must(answers).map((q): AlumniAnswer => ({
          id: q.id,
          alumnusId: q.alumnus_id,
          question: q.question,
          answer: q.answer,
          askedBy: q.asked_by,
        })),
      };
    },
  });
}

/** Questions the student asked (their own), or every question for the office that answers. */
export function useAlumniQuestions(filter: { studentId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['alumni-questions', filter],
    queryFn: async (): Promise<AlumniQuestion[]> => {
      let query = db
        .from('alumni_questions')
        .select('id, alumnus_id, question, answer, asked_at')
        .order('asked_at', { ascending: false });
      query =
        'studentId' in filter
          ? query.eq('student_member_id', filter.studentId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((q) => ({
        id: q.id,
        alumnusId: q.alumnus_id,
        question: q.question,
        answer: q.answer,
        askedAt: q.asked_at,
      }));
    },
  });
}

export function useAlumniActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['alumni-questions'] });
    void client.invalidateQueries({ queryKey: ['alumni', schoolId] });
  };
  return {
    ask: useMutation({
      mutationFn: async (a: { alumnusId: string; studentId: string; question: string }) =>
        must(
          await db.from('alumni_questions').insert({
            school_id: schoolId,
            alumnus_id: a.alumnusId,
            student_member_id: a.studentId,
            question: a.question.trim(),
          }),
        ),
      onSettled: invalidate,
    }),
    answer: useMutation({
      mutationFn: async (a: { id: string; answer: string }) =>
        must(
          await db
            .from('alumni_questions')
            .update({ answer: a.answer.trim(), answered_at: new Date().toISOString() })
            .eq('id', a.id),
        ),
      onSettled: invalidate,
    }),
  };
}
